import React, { useState, useEffect } from "react";
import { FaCircleHalfStroke } from "react-icons/fa6";

const themes = {
  "blue-yellow": {
    background: "#08306b",
    foreground: "#fffd33",
  },
  "black-white": {
    background: "#000000",
    foreground: "#ffffff",
  },
  "white-black": {
    background: "#ffffff",
    foreground: "#000000",
  },
};

const content = {
  en: {
    title: "LANDING PAGE",
    subtitle: "VISION by Metron Ariston-Making Responsibility a Trend™. All rights reserved.",
    body: `VISION by Metron Ariston™ is a pioneering Greek initiative that brings inclusion to the forefront of contemporary hospitality. It aims to make hospitality venues — from bars and restaurants to cafés and hotels — more accessible to people who are blind or visually impaired.

Developed by Metron Ariston – Making Responsibility a Trend, with the voluntary scientific contribution of advisors, it is based on hybrid (audio-visual) menus narrated by renowned radio hosts and actors. It promotes autonomy and equal access to hospitality for people who are blind or visually impaired.

Now entering its Pilot Phase I across 12 top-tier venues in Greece, VISION combines human-centered design with a fully integrated feedback mechanism to support continuous improvement. More than a tool, it is a living system of inclusion.

VISION by Metron Ariston™ aspires to become an international benchmark — redefining hospitality as a shared cultural right: accessible, respectful, and deeply human.`,
  },
  gr: {
    title: "LANDING PAGE",
    subtitle: "VISION by Metron Ariston-Making Responsibility a Trend™. All rights reserved.",
    body: `Το VISION by Metron Ariston™ είναι μια πρωτοποριακή ελληνική πρωτοβουλία που φέρνει την ένταξη στο επίκεντρο της σύγχρονης φιλοξενίας. Στοχεύει να καταστήσει τους χώρους φιλοξενίας — από bar και εστιατόρια έως καφέ και ξενοδοχεία — περισσότερο προσβάσιμους για άτομα με οπτική αναπηρία.

Έχει αναπτυχθεί από τη Metron Ariston – Making Responsibility a Trend, με την εθελοντική επιστημονική συνδρομή συμβούλων, και βασίζεται σε υβριδικούς (οπτικοακουστικούς) καταλόγους με αφηγήσεις από αναγνωρισμένους ραδιοφωνικούς παραγωγούς και ηθοποιούς. Προάγει την αυτονομία και την ισότιμη πρόσβαση στην εστίαση για άτομα με οπτική αναπηρία.

Καθώς εισέρχεται στην Πιλοτική Φάση Ι σε 12 κορυφαίους χώρους φιλοξενίας στην Ελλάδα, το VISION συνδυάζει ανθρωποκεντρικό σχεδιασμό με έναν πλήρως ενσωματωμένο μηχανισμό ανατροφοδότησης, υποστηρίζοντας τη συνεχή βελτίωση. Δεν είναι απλώς ένα εργαλείο — είναι ένα ζωντανό σύστημα ένταξης.

Το VISION by Metron Ariston™ φιλοδοξεί να αποτελέσει διεθνές πρότυπο, επαναπροσδιορίζοντας τη φιλοξενία ως ένα κοινό πολιτιστικό δικαίωμα — προσβάσιμο, με σεβασμό και βαθιά ανθρώπινο.`,
  },
};

function LandingPage() {
  const [lang, setLang] = useState("gr");
  const [theme, setTheme] = useState("blue-yellow");
  const [fadeIn, setFadeIn] = useState(false);

  const colors = themes[theme];

  useEffect(() => {
    setTimeout(() => setFadeIn(true), 100);
  }, []);

  return (
    <div style={{ ...styles.page, backgroundColor: colors.background }}>
      <div
        style={{
          ...styles.contentBox,
          color: colors.foreground,
          border: `2px solid ${colors.foreground}`,
          opacity: fadeIn ? 1 : 0,
          transform: fadeIn ? "translateY(0)" : "translateY(20px)",
        }}
      >
        <div style={styles.topControls}>
          <div style={styles.languageToggle}>
            <button
              onClick={() => setLang("en")}
              style={{
                ...styles.toggleButton,
                ...(lang === "en"
                  ? { backgroundColor: colors.foreground, color: colors.background }
                  : { backgroundColor: "transparent", color: colors.foreground }),
                borderColor: colors.foreground,
              }}
            >
              EN
            </button>
            <button
              onClick={() => setLang("gr")}
              style={{
                ...styles.toggleButton,
                ...(lang === "gr"
                  ? { backgroundColor: colors.foreground, color: colors.background }
                  : { backgroundColor: "transparent", color: colors.foreground }),
                borderColor: colors.foreground,
              }}
            >
              GR
            </button>
          </div>

          <button
            onClick={() =>
              setTheme((prev) =>
                prev === "blue-yellow"
                  ? "black-white"
                  : prev === "black-white"
                  ? "white-black"
                  : "blue-yellow"
              )
            }
            style={{
              ...styles.themeButton,
              borderColor: colors.foreground,
              color: colors.foreground,
            }}
            title="Toggle Theme"
          >
            <FaCircleHalfStroke />
          </button>
        </div>

        <h1 style={styles.title}>{content[lang].title}</h1>
        <h2 style={styles.subtitle}>{content[lang].subtitle}</h2>
        <p style={styles.body}>{content[lang].body}</p>

        <div style={styles.logoContainer}>
          <img
            src="/VISION_CAMPARI.png"
            alt="Metron Ariston Secondary Logo"
            style={{
            height: "160px",
            width: "auto",
            opacity: 0.9,
            filter:
            theme === "blue-yellow"
              ? "brightness(110%) contrast(105%)"
              : theme === "black-white"
              ? "invert(1)"
              : "none",
            transition: "filter 0.5s ease",
          }}
          />
        </div>
      </div>
    </div>
  );
}

const styles = {
  page: {
    minHeight: "100vh",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: "16px",
    fontFamily: "'Segoe UI', sans-serif",
  },
  contentBox: {
    backgroundColor: "#ffffff11",
    backdropFilter: "blur(10px)",
    borderRadius: "16px",
    padding: "20px",
    maxWidth: "420px",
    width: "90%",
    boxShadow: "0 4px 12px rgba(0,0,0,0.25)",
    transition: "all 0.6s ease",
    marginBottom: "20px",
  },
  topControls: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "12px",
  },
  languageToggle: {
    display: "flex",
    gap: "8px",
  },
  toggleButton: {
    border: "2px solid",
    padding: "5px 10px",
    borderRadius: "6px",
    fontWeight: "bold",
    cursor: "pointer",
    fontSize: "13px",
    transition: "all 0.3s ease",
  },
  themeButton: {
    border: "2px solid",
    backgroundColor: "transparent",
    fontWeight: "bold",
    borderRadius: "50%",
    width: "34px",
    height: "34px",
    fontSize: "16px",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  title: {
    fontSize: "22px",
    marginBottom: "8px",
    textAlign: "center",
    letterSpacing: "0.5px",
  },
  subtitle: {
    fontSize: "17px",
    marginBottom: "12px",
    textAlign: "center",
    fontWeight: "500",
  },
  body: {
    whiteSpace: "pre-line",
    fontSize: "16px",
    lineHeight: "1.5",
    textAlign: "justify",
    marginBottom: "8px",
  },
  logoContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    marginTop: "4px",
  },
};

export default LandingPage;